/**
 * Client area and staff project pages (docs/specs/portal/client-area.md). Reads go through RLS; the client loaders
 * also filter on exactly what the client policies allow (organisation, not deleted, published) so Preview as client,
 * which runs with the staff member's wider rights, shows the organisation's view row for row
 * (supabase/tests/rls-portal.test.ts compares the two).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  reportStoragePath,
  type Block,
  type LineStatus,
  type PlannedLine,
  type Report,
  type ReportEmailOptions,
} from '@teslyn/domain';

/** Whose projects a page shows: one client organisation (client users and Preview as client), or the tenant (staff). */
export type Scope =
  | { readonly kind: 'client'; readonly clientOrgId: string }
  | { readonly kind: 'tenant'; readonly tenantId: string };

export interface PortalProject {
  readonly id: string;
  readonly tenantId: string;
  readonly number: string;
  readonly title: string;
  readonly area: string;
  readonly status: string;
  readonly epsg: number | null;
  readonly clientOrgId: string | null;
  readonly clientOrgName: string | null;
  /** The client's name as Teslyn Field holds it (text), whether or not an organisation is linked. */
  readonly clientName: string;
}

export interface ProjectPlan {
  readonly blocks: readonly Block[];
  readonly plan: readonly PlannedLine[];
  readonly statuses: readonly LineStatus[];
}

export interface Publication {
  readonly storagePath: string;
  readonly fileName: string;
  readonly sizeBytes: number;
  readonly published: boolean;
  readonly publishedAt: string | null;
  /** Who put the PDF in the cloud: Teslyn Field after the report synced, or staff with "Attach PDF". */
  readonly source: 'field' | 'portal';
}

export interface PortalReport {
  readonly id: string;
  readonly projectId: string;
  readonly kind: Report['kind'];
  readonly period: string;
  readonly revision: number;
  readonly generatedAt: string;
  /** Null when no PDF has been attached in the cloud yet. */
  readonly publication: Publication | null;
}

export interface Res<T> {
  readonly data: T | null;
  readonly error: { readonly message: string; readonly code?: string } | null;
}

function one<T>(v: T | T[] | null | undefined): T | null {
  return Array.isArray(v) ? (v[0] ?? null) : (v ?? null);
}

function must<T>(r: Res<T>): T {
  if (r.error) throw new Error(plainPortalError(r.error));
  return r.data as T;
}

/** Database and Storage errors in plain words. */
export function plainPortalError(error: { readonly message: string; readonly code?: string }): string {
  if (error.code === '42501' || /row-level security|permission denied/i.test(error.message)) {
    return 'You do not have permission to do this.';
  }
  return error.message;
}

const PAGE = 1000;

/** Every row of a query, page by page (PostgREST answers at most 1,000 rows at a time). */
export async function all<T>(page: (from: number, to: number) => PromiseLike<Res<T[]>>): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const rows = must(await page(from, from + PAGE - 1)) ?? [];
    out.push(...rows);
    if (rows.length < PAGE) return out;
  }
}

interface ProjectRow {
  id: string;
  tenant_id: string;
  number: string;
  title: string;
  area: string | null;
  status: string;
  epsg: number | null;
  client_org_id: string | null;
  client_name?: string | null;
  client_org?: { name: string } | { name: string }[] | null;
  /** client_project's own column (security review M4) */
  client_org_name?: string | null;
}

/**
 * "The view is not there": the database update of 30 Oct 2026 (20261030130100_client_views.sql) is not applied yet,
 * so the client pages read the tables as before (Teslyn Cloud can be published before the database is updated).
 */
export const missingView = (e: { readonly code?: string; readonly message?: string } | null): boolean =>
  Boolean(
    e &&
    (e.code === 'PGRST205' ||
      e.code === '42P01' ||
      /could not find the table|relation .* does not exist/i.test(e.message ?? '')),
  );

export async function loadProjects(sb: SupabaseClient, scope: Scope): Promise<PortalProject[]> {
  // A client reads `client_project` (security review M4: only these columns ever reach a client's browser); Preview as
  // client reads it too, with the staff member's rights, so both see the same rows and columns.
  const query = (view: boolean) => {
    const q = view
      ? sb
          .from('client_project')
          .select(
            'id, tenant_id, number, title, area, status, epsg, client_org_id, client_name, client_org_name',
          )
          .is('deleted_at', null)
      : sb
          .from('project')
          .select(
            'id, tenant_id, number, title, area, status, epsg, client_org_id, client_name, client_org:client_org_id (name)',
          )
          .is('deleted_at', null);
    // clients never see archived projects (decision 418); the policies refuse them too, and Preview as client, which
    // runs with the staff member's rights, shows exactly the same
    return (scope.kind === 'client'
      ? q.eq('client_org_id', scope.clientOrgId).neq('status', 'archived')
      : q.eq('tenant_id', scope.tenantId)
    ).order('number') as unknown as PromiseLike<Res<ProjectRow[]>>;
  };
  let res = await query(scope.kind === 'client');
  if (scope.kind === 'client' && missingView(res.error)) res = await query(false);
  const rows = must(res) ?? [];
  return rows.map((r) => ({
    id: r.id,
    tenantId: r.tenant_id,
    number: r.number,
    title: r.title,
    area: r.area ?? '',
    status: r.status,
    epsg: r.epsg,
    clientOrgId: r.client_org_id,
    clientOrgName: r.client_org_name ?? one(r.client_org)?.name ?? null,
    clientName: r.client_name ?? '',
  }));
}

/** The block columns the maps and progress pages read (named, never `*`: security review M4). */
export const BLOCK_COLUMNS =
  'id, tenant_id, project_id, name, key, digit, kind, epsg, terrain_clearance_m, trav_spacing_m, trav_direction_deg, tie_spacing_m, tie_direction_deg, planned_km, boundary, created_at, updated_at, updated_by, deleted_at, report_include, km_completed';
/** The planned line columns the maps and progress pages read. */
export const PLANNED_LINE_COLUMNS =
  'id, tenant_id, project_id, block_id, number, kind, x1, y1, x2, y2, length_m, reflight_of_line_id, created_at, updated_at, updated_by, deleted_at, segment_from_m, segment_to_m, lines_file_id, replaces_line_id';
/** What a client reads of a line's status (`client_line_status`, security review M4). */
export const CLIENT_LINE_STATUS_COLUMNS =
  'id, tenant_id, project_id, planned_line_id, line_number, acceptance, accepted_m, use_km, start_along_m, end_along_m, updated_at, deleted_at';

/** A row of `client_line_status` as a LineStatus: the columns a client never receives are empty. */
export function clientLineStatus(r: Record<string, unknown>): LineStatus {
  return {
    flight_id: '',
    flown_m: 0,
    proposed_m: 0,
    reason: '',
    note: '',
    reviewed_by: null,
    reviewed_at: null,
    reflight_of_status_id: null,
    direction_deg: null,
    created_at: String(r.updated_at ?? ''),
    updated_by: null,
    ...r,
  } as unknown as LineStatus;
}

/**
 * Blocks, planned lines and line status of one project (live rows only, as clients read them). `client` (a client
 * user, or Preview as client) reads `client_line_status`: no reviewer's reason or note, who reviewed, or log markers.
 */
export async function loadProjectPlan(
  sb: SupabaseClient,
  projectId: string,
  mode: 'staff' | 'client' = 'staff',
): Promise<ProjectPlan> {
  const [blocks, plan, statuses] = await Promise.all([
    all<Block>((from, to) =>
      sb
        .from('block')
        .select(BLOCK_COLUMNS)
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('name')
        .range(from, to),
    ),
    all<PlannedLine>((from, to) =>
      sb
        .from('planned_line')
        .select(PLANNED_LINE_COLUMNS)
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('number')
        .range(from, to),
    ),
    mode === 'client'
      ? all<Record<string, unknown>>((from, to) =>
          sb
            .from('client_line_status')
            .select(CLIENT_LINE_STATUS_COLUMNS)
            .eq('project_id', projectId)
            .order('id')
            .range(from, to),
        ).then(
          (rows) => rows.map(clientLineStatus),
          (e: unknown) =>
            // before the database update: the table, with the same columns
            missingView({ message: e instanceof Error ? e.message : String(e) })
              ? all<Record<string, unknown>>((from, to) =>
                  sb
                    .from('line_status')
                    .select(CLIENT_LINE_STATUS_COLUMNS)
                    .eq('project_id', projectId)
                    .is('deleted_at', null)
                    .order('id')
                    .range(from, to),
                ).then((rows) => rows.map(clientLineStatus))
              : Promise.reject(e),
        )
      : all<LineStatus>((from, to) =>
          sb
            .from('line_status')
            .select('*')
            .eq('project_id', projectId)
            .is('deleted_at', null)
            .order('id')
            .range(from, to),
        ),
  ]);
  return { blocks, plan, statuses };
}

interface PublicationRow {
  report_id: string;
  project_id: string;
  storage_path: string;
  file_name: string;
  size_bytes: number;
  published: boolean;
  published_at: string | null;
  source?: 'field' | 'portal';
}
interface ReportRow {
  id: string;
  project_id: string;
  kind: Report['kind'];
  period: string;
  revision: number;
  generated_at: string;
}

const publicationOf = (p: PublicationRow | null): Publication | null =>
  p
    ? {
        storagePath: p.storage_path,
        fileName: p.file_name,
        sizeBytes: Number(p.size_bytes),
        published: p.published,
        publishedAt: p.published_at,
        source: p.source ?? 'portal',
      }
    : null;

const newestFirst = (a: PortalReport, b: PortalReport): number =>
  b.generatedAt.localeCompare(a.generatedAt) || b.period.localeCompare(a.period) || b.revision - a.revision;

/** Published, live reports of these projects: what a client sees. */
export async function loadPublishedReports(
  sb: SupabaseClient,
  projectIds: readonly string[],
): Promise<PortalReport[]> {
  if (projectIds.length === 0) return [];
  // the reports themselves from `client_report` (security review M4: no manifest, PDF path or who generated it)
  const [rows, reports] = await Promise.all([
    all<PublicationRow>((from, to) =>
      sb
        .from('report_publication')
        .select('report_id, project_id, storage_path, file_name, size_bytes, published, published_at, source')
        .eq('published', true)
        .in('project_id', projectIds)
        .order('report_id')
        .range(from, to),
    ),
    all<ReportRow>((from, to) =>
      sb
        .from('client_report')
        .select('id, project_id, kind, period, revision, generated_at')
        .in('project_id', projectIds)
        .order('id')
        .range(from, to),
    ).catch((e: unknown) =>
      // before the database update: the table, with the same columns
      missingView({ message: e instanceof Error ? e.message : String(e) })
        ? all<ReportRow>((from, to) =>
            sb
              .from('report')
              .select('id, project_id, kind, period, revision, generated_at')
              .in('project_id', projectIds)
              .is('deleted_at', null)
              .order('id')
              .range(from, to),
          )
        : Promise.reject(e),
    ),
  ]);
  const byId = new Map(reports.map((r) => [r.id, r]));
  const out: PortalReport[] = [];
  for (const r of rows) {
    const rep = byId.get(r.report_id);
    if (!rep || !r.published) continue;
    out.push({
      id: r.report_id,
      projectId: r.project_id,
      kind: rep.kind,
      period: rep.period,
      revision: rep.revision,
      generatedAt: rep.generated_at,
      publication: publicationOf(r),
    });
  }
  return out.sort(newestFirst);
}

/** Every live report of these projects with its publication, if any: what staff see. */
export async function loadStaffReports(
  sb: SupabaseClient,
  projectIds: readonly string[],
): Promise<PortalReport[]> {
  if (projectIds.length === 0) return [];
  const [reports, pubs] = await Promise.all([
    all<ReportRow>((from, to) =>
      sb
        .from('report')
        .select('id, project_id, kind, period, revision, generated_at')
        .in('project_id', projectIds)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    all<PublicationRow>((from, to) =>
      sb
        .from('report_publication')
        .select('report_id, project_id, storage_path, file_name, size_bytes, published, published_at, source')
        .in('project_id', projectIds)
        .order('report_id')
        .range(from, to),
    ),
  ]);
  const byReport = new Map(pubs.map((p) => [p.report_id, p]));
  return reports
    .map((r) => ({
      id: r.id,
      projectId: r.project_id,
      kind: r.kind,
      period: r.period,
      revision: r.revision,
      generatedAt: r.generated_at,
      publication: publicationOf(byReport.get(r.id) ?? null),
    }))
    .sort(newestFirst);
}

export const MAX_PDF_BYTES = 50 * 1024 * 1024;

/** SHA-256 of a chosen file in lower-case hex, or null where the browser cannot hash it (the audit then has none). */
export async function fileSha256(file: Blob): Promise<string | null> {
  try {
    const buf = await new Promise<ArrayBuffer>((ok, fail) => {
      const r = new FileReader();
      r.onload = () => ok(r.result as ArrayBuffer);
      r.onerror = () => fail(r.error ?? new Error('read failed'));
      r.readAsArrayBuffer(file);
    });
    const digest = await globalThis.crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

/** Upload a report's PDF to its Storage path (replacing an earlier one) and record it, unpublished if new. */
export async function attachPdf(
  sb: SupabaseClient,
  project: Pick<PortalProject, 'id' | 'tenantId'>,
  reportId: string,
  file: File,
): Promise<void> {
  if (!/\.pdf$/i.test(file.name) || (file.type && file.type !== 'application/pdf')) {
    throw new Error('Choose a PDF file.');
  }
  if (file.size === 0) throw new Error('The file is empty.');
  if (file.size > MAX_PDF_BYTES) throw new Error('The PDF is larger than 50 MB.');
  const path = reportStoragePath(project.tenantId, project.id, reportId);
  const up = await sb.storage.from('reports').upload(path, file, {
    upsert: true,
    contentType: 'application/pdf',
  });
  if (up.error) throw new Error(plainPortalError(up.error));
  must(
    (await sb.from('report_publication').upsert(
      {
        report_id: reportId,
        tenant_id: project.tenantId,
        project_id: project.id,
        storage_path: path,
        file_name: file.name,
        size_bytes: file.size,
        // staff attached it: Teslyn Field leaves it as it is (docs/specs/cloud/storage-uploads.md §1)
        source: 'portal',
        sha256: await fileSha256(file),
      },
      { onConflict: 'report_id' },
    )) as Res<unknown>,
  );
}

/**
 * Publish to the project's client organisation, or unpublish (the PDF stays). Audited by the database. With Publish,
 * `email` is the publisher's choice for the client's email (decisions 883, 884: send or not, words, contents, the
 * daily report a weekly one carries), kept with the publication for the database's trigger. A database before
 * 20261030100000 has no such column: the report is then published with the project's email, and the answer says so.
 */
export async function setPublished(
  sb: SupabaseClient,
  reportId: string,
  published: boolean,
  email?: ReportEmailOptions,
): Promise<{ readonly emailChoiceKept: boolean }> {
  const write = async (row: Record<string, unknown>) =>
    (await sb.from('report_publication').update(row).eq('report_id', reportId).select('report_id')) as Res<
      unknown[]
    >;
  let res = await write(published && email ? { published, email_options: email } : { published });
  let kept = Boolean(published && email);
  if (kept && res.error && (res.error.code === 'PGRST204' || /email_options/.test(res.error.message))) {
    res = await write({ published });
    kept = false;
  }
  if (must(res)?.length === 0) throw new Error('Nothing was changed. You may not have permission.');
  return { emailChoiceKept: kept };
}

/** A resupply of the project and its delivery docket in the cloud, if Teslyn Field has uploaded it (decision 193). */
export interface FuelDocket {
  readonly entryId: string;
  readonly date: string;
  readonly time: string;
  readonly location: string;
  readonly upliftTruckAvgasL: number | null;
  readonly upliftTruckTurbineL: number | null;
  readonly upliftDrumsAvgas: number | null;
  readonly upliftDrumsTurbine: number | null;
  readonly file: {
    readonly bucket: string;
    readonly objectPath: string;
    readonly fileName: string;
    readonly contentType: string;
    readonly sizeBytes: number;
    readonly source: 'field' | 'portal';
    readonly uploadedAt: string;
  } | null;
}

interface FuelEntryRow {
  id: string;
  date: string;
  time: string;
  location: string;
  uplift_truck_avgas_l: number | null;
  uplift_truck_turbine_l: number | null;
  uplift_drums_avgas: number | null;
  uplift_drums_turbine: number | null;
  docket_path: string | null;
}
interface ProjectFileRow {
  record_id: string;
  bucket: string;
  object_path: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  source: 'field' | 'portal';
  uploaded_at: string;
}

/** The project's live resupplies that carry a docket, newest first, with the file the cloud holds for each. */
export async function loadFuelDockets(sb: SupabaseClient, projectId: string): Promise<FuelDocket[]> {
  const [entries, files] = await Promise.all([
    all<FuelEntryRow>((from, to) =>
      sb
        .from('fuel_ledger_entry')
        .select(
          'id, date, time, location, uplift_truck_avgas_l, uplift_truck_turbine_l, uplift_drums_avgas, uplift_drums_turbine, docket_path',
        )
        .eq('project_id', projectId)
        .eq('kind', 'resupply')
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    all<ProjectFileRow>((from, to) =>
      sb
        .from('project_file')
        .select('record_id, bucket, object_path, file_name, content_type, size_bytes, source, uploaded_at')
        .eq('project_id', projectId)
        .eq('kind', 'fuel_docket')
        .order('record_id')
        .range(from, to),
    ),
  ]);
  const byEntry = new Map(files.map((f) => [f.record_id, f]));
  return entries
    .filter((e) => e.docket_path)
    .map((e) => {
      const f = byEntry.get(e.id);
      return {
        entryId: e.id,
        date: e.date,
        time: e.time,
        location: e.location,
        upliftTruckAvgasL: e.uplift_truck_avgas_l,
        upliftTruckTurbineL: e.uplift_truck_turbine_l,
        upliftDrumsAvgas: e.uplift_drums_avgas,
        upliftDrumsTurbine: e.uplift_drums_turbine,
        file: f
          ? {
              bucket: f.bucket,
              objectPath: f.object_path,
              fileName: f.file_name,
              contentType: f.content_type,
              sizeBytes: Number(f.size_bytes),
              source: f.source,
              uploadedAt: f.uploaded_at,
            }
          : null,
      };
    })
    .sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));
}

export type PreviewAction = 'started' | 'ended' | 'report_opened';

/** Audit row for Preview as client, written as the signed-in staff member (portal_preview_audit). */
export async function previewAudit(
  sb: SupabaseClient,
  clientOrgId: string,
  action: PreviewAction,
  detail: Record<string, unknown> = {},
): Promise<void> {
  const { error } = await sb.rpc('portal_preview_audit', {
    p_client_org: clientOrgId,
    p_action: action,
    p_detail: detail,
  });
  if (error) throw new Error(plainPortalError(error));
}

export interface OrgOption {
  readonly id: string;
  readonly name: string;
}

export async function loadClientOrgs(sb: SupabaseClient, tenantId: string): Promise<OrgOption[]> {
  return (
    must(
      (await sb
        .from('client_org')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null)
        .order('name')) as Res<OrgOption[]>,
    ) ?? []
  );
}

export interface OrgMember {
  readonly userId: string;
  readonly email: string | null;
  readonly displayName: string;
  readonly role: 'client_admin' | 'client_viewer';
}

export async function loadOrgMembers(sb: SupabaseClient, clientOrgId: string): Promise<OrgMember[]> {
  const rows =
    must(
      (await sb
        .from('client_membership')
        .select('user_id, role, app_user:user_id (email, display_name)')
        .eq('client_org_id', clientOrgId)) as Res<
        {
          user_id: string;
          role: string;
          app_user:
            { email: string; display_name: string } | { email: string; display_name: string }[] | null;
        }[]
      >,
    ) ?? [];
  return rows
    .filter((r) => r.role === 'client_admin' || r.role === 'client_viewer')
    .map((r) => {
      const u = one(r.app_user);
      return {
        userId: r.user_id,
        email: u?.email ?? null,
        displayName: u?.display_name ?? '',
        role: r.role as OrgMember['role'],
      };
    })
    .sort((a, b) => a.role.localeCompare(b.role) || (a.email ?? '~').localeCompare(b.email ?? '~'));
}

/**
 * Remove a viewer of the caller's own organisation (RLS: client admins remove viewers only). The database writes
 * the audit row (trigger client_membership_client_audit), since a client user cannot write audit_event.
 */
export async function removeViewer(sb: SupabaseClient, clientOrgId: string, userId: string): Promise<void> {
  const res = (await sb
    .from('client_membership')
    .delete()
    .eq('client_org_id', clientOrgId)
    .eq('user_id', userId)
    .eq('role', 'client_viewer')
    .select('user_id')) as Res<unknown[]>;
  if (must(res)?.length === 0) {
    throw new Error('Nothing was changed. You may not have permission, or the viewer was already removed.');
  }
}

// ---------------------------------------------------------------- project lifecycle (project-lifecycle.md §5)

/**
 * Link a project to the client organisation whose portal shows it, or unlink it (null). Tenant admins, managers and
 * office staff (policy project_write); the database refuses everyone else by changing nothing. Nothing published or
 * shared reaches a client until the project is linked (MB's R99, 26 Oct 2026).
 */
export async function setProjectClientOrg(
  sb: SupabaseClient,
  projectId: string,
  clientOrgId: string | null,
): Promise<void> {
  const res = (await sb
    .from('project')
    .update({ client_org_id: clientOrgId })
    .eq('id', projectId)
    .select('id')) as Res<unknown[]>;
  if (must(res)?.length === 0) throw new Error('Nothing was changed. You may not have permission.');
}

/** Change a project's status; the database decides who may (decisions 416, 423) and audits it. */
export async function setProjectStatus(sb: SupabaseClient, projectId: string, status: string): Promise<void> {
  const { error } = await sb.from('project').update({ status }).eq('id', projectId);
  if (error) throw new Error(plainPortalError(error));
}

/** Whether deleting the project would put it in the Deleted bin (it holds field data) or delete it at once. */
export async function projectHasFieldData(sb: SupabaseClient, projectId: string): Promise<boolean> {
  const { data, error } = await sb.rpc('project_has_field_data', { p_project: projectId });
  if (error) throw new Error(plainPortalError(error));
  return data === true;
}

export interface DeleteOutcome {
  readonly outcome: 'deleted' | 'binned';
  readonly purge_after: string;
}

/** Delete a project (tenant admins, decision 417): the number typed is checked by the database too. */
export async function deleteProject(
  sb: SupabaseClient,
  projectId: string,
  typed: string,
): Promise<DeleteOutcome> {
  const { data, error } = await sb.rpc('project_delete', { p_project: projectId, p_typed_number: typed });
  if (error) throw new Error(plainPortalError(error));
  return data as DeleteOutcome;
}

export async function restoreProject(sb: SupabaseClient, projectId: string): Promise<void> {
  const { error } = await sb.rpc('project_restore', { p_project: projectId });
  if (error) throw new Error(plainPortalError(error));
}

export interface ProjectDeletion {
  readonly id: string;
  readonly projectId: string;
  readonly number: string;
  readonly title: string;
  readonly outcome: 'deleted' | 'binned';
  readonly fieldData: boolean;
  readonly deletedAt: string;
  readonly purgeAfter: string;
  readonly restoredAt: string | null;
  readonly purgedAt: string | null;
  readonly deletedBy: string | null;
}

interface DeletionRow {
  id: string;
  project_id: string;
  number: string;
  title: string;
  outcome: 'deleted' | 'binned';
  field_data: boolean;
  deleted_at: string;
  purge_after: string;
  restored_at: string | null;
  purged_at: string | null;
  deleted_by_user: { email: string } | { email: string }[] | null;
}

/** The tenant's deletions (readable by tenant admins only), newest first. */
export async function loadDeletions(sb: SupabaseClient, tenantId: string): Promise<ProjectDeletion[]> {
  const rows =
    must(
      (await sb
        .from('project_deletion')
        .select(
          'id, project_id, number, title, outcome, field_data, deleted_at, purge_after, restored_at, purged_at, deleted_by_user:deleted_by (email)',
        )
        .eq('tenant_id', tenantId)
        .order('deleted_at', { ascending: false })) as Res<DeletionRow[]>,
    ) ?? [];
  return rows.map((r) => ({
    id: r.id,
    projectId: r.project_id,
    number: r.number,
    title: r.title,
    outcome: r.outcome,
    fieldData: r.field_data,
    deletedAt: r.deleted_at,
    purgeAfter: r.purge_after,
    restoredAt: r.restored_at,
    purgedAt: r.purged_at,
    deletedBy: one(r.deleted_by_user)?.email ?? null,
  }));
}
